import {isFeatureEnabled} from 'feature-flags';
import {Action} from 'platform/components';
import {Show, Text, VStack} from 'platform/foundation';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetCebiaAutotracerAndReportConfigQuery} from '@dms/api/core';
import {CebiaUrlResponseBody, FullVehicle, FullVehicleResponseBody} from '@dms/api/shared';
import {useGetLastAutotracerReportQuery, useLazyGetAutoTracerUrlQuery} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {settingsRoutes, vehiclesRoutes} from '@dms/routes';
import {handleApiError, Widget} from '@dms/shared';

import {
  buildArray,
  composePath,
  openInNewTab,
  suffixTestId,
  TestIdProps,
  useNavigate,
} from 'shared/web';

import {CebiaSmartCodeUrlPublishSwitch} from './components/CebiaSmartCodeUrlPublishSwitch';
import {CebiaWidgetAlert} from './components/CebiaWidgetAlert';
import {CebiaWidgetLogo} from './components/CebiaWidgetLogo';
import {getIsSmartCodeUrlPublishSwitchShown} from './getIsSmartCodeUrlPublishSwitchShown';

interface CebiaAutotracerVehicleParams {
  id?: string;
  vin?: string;
  make?: string;
  modelFamily?: string;
}

interface CebiaAutotracerWidgetProps extends TestIdProps {
  vehicle: FullVehicle | CebiaAutotracerVehicleParams | FullVehicleResponseBody;
  isSmartCodeUrlPublishSwitchShown?: boolean;
}

export function CebiaAutotracerWidget(props: CebiaAutotracerWidgetProps) {
  const navigate = useNavigate();
  const [getAutoTracerUrl, {isLoading}] = useLazyGetAutoTracerUrlQuery();

  const {vin, make, modelFamily, id: vehicleId} = props.vehicle;

  const {data: newestReport, isLoading: isLastReportLoading} = useGetLastAutotracerReportQuery(
    {vehicleId: vehicleId!},
    {skip: isNil(vehicleId) || isNil(vin)}
  );

  const isVehicleValid = vin && make && modelFamily;

  const {data: cebiaAutotracerAndReportConfig} = useGetCebiaAutotracerAndReportConfigQuery();

  const isCredentialsValid = isNotNilOrEmpty(cebiaAutotracerAndReportConfig?.credentials?.username);

  const getNewReport = () => {
    getAutoTracerUrl({
      vin: vin!,
      vehicleMark: make,
      vehicleModel: modelFamily,
      vehicleId,
    })
      .unwrap()
      .then((response: CebiaUrlResponseBody) => {
        openInNewTab(response.url);
      })
      .catch(handleApiError);
  };

  if (!cebiaAutotracerAndReportConfig?.enabled) {
    return null;
  }

  const hasNewestReport = isNotNil(newestReport);
  const isSmartCodeUrlPublishSwitchVisible =
    hasNewestReport &&
    getIsSmartCodeUrlPublishSwitchShown(props) &&
    isFeatureEnabled(featureFlags.SALES_CEBIA_SMART_CODE_URL_PUBLISHING_SWITCH);
  const isReportButtonDisabled = !isCredentialsValid || !isVehicleValid;

  const widgetActions = buildArray<Action>()
    .when(isSmartCodeUrlPublishSwitchVisible, {
      type: 'custom',
      component: (
        <CebiaSmartCodeUrlPublishSwitch
          vehicleId={vehicleId!}
          label={i18n.t('page.integrations.labels.cebiaSmartCodeUrlPublishInAdvertisement')}
        />
      ),
    })
    .when(hasNewestReport, {
      type: 'button',
      variant: 'secondary',
      rightIcon: 'action/launch',
      onClick: () => window.open(newestReport!.url),
      title: i18n.t('page.integrations.labels.cebiaAutotracerViewReport'),
      'data-testid': suffixTestId('cebiaAutotracerWidget-viewReport', props),
    })
    .when(hasNewestReport, {
      type: 'button',
      variant: 'secondary',
      isDisabled: isReportButtonDisabled,
      isLoading,
      onClick: getNewReport,
      title: i18n.t('page.integrations.labels.cebiaAutotracerNewReport'),
      'data-testid': suffixTestId('cebiaAutotracerWidget-newReport', props),
    })
    .whenNot(hasNewestReport, {
      type: 'button',
      variant: 'secondary',
      isDisabled: isReportButtonDisabled,
      isLoading,
      onClick: getNewReport,
      title: i18n.t('page.integrations.labels.cebiaAutotraceGetReport'),
      'data-testid': suffixTestId('cebiaAutotracerWidget-getReport', props),
    });

  return (
    <VStack spacing={2}>
      <Widget.Card data-testid={suffixTestId('cebiaAutotracerWidget', props)}>
        <Widget.Header
          logo={<CebiaWidgetLogo alt={i18n.t('page.integrations.labels.cebiaAutotraceTitle')} />}
          title={i18n.t('page.integrations.labels.cebiaAutotraceTitle')}
          subtitle={
            <Text size="xSmall">
              {i18n.t('page.integrations.labels.cebiaAutotraceWidgetDescription')}
            </Text>
          }
          actions={widgetActions}
          areActionsLoading={isLastReportLoading}
          data-testid={suffixTestId('cebiaAutotracerWidget', props)}
        />
      </Widget.Card>
      <Show when={!isVehicleValid}>
        <CebiaWidgetAlert
          colorScheme="warning"
          message={i18n.t('page.integrations.labels.cebiaAutotraceRequiredFields')}
          actionText={i18n.t('entity.vehicle.labels.editVehicle')}
          actionCallback={
            vehicleId
              ? () => navigate(composePath(vehiclesRoutes.editV2, {params: {id: vehicleId}}))
              : undefined
          }
        />
      </Show>
      <Show when={!isCredentialsValid}>
        <CebiaWidgetAlert
          colorScheme="error"
          message={i18n.t('general.errors.invalidCredentials')}
          actionText={i18n.t('general.labels.settings')}
          actionCallback={() => navigate(settingsRoutes.vehicleServicesCebia)}
        />
      </Show>
    </VStack>
  );
}
